import { z } from "zod";

import type {
	CompetencyAssignmentSourceFilter,
	CompetencyAssignmentStatusFilter,
	CompetencyLevelGap,
} from "../../../../api/services/defs/competency-assignments";

import { ASSIGNMENT_STATUS_LABELS, ASSIGNMENT_STATUS_OPTIONS, DEFAULT_STATUS } from "../../../../utils/statusFilter";

export type { CompetencyAssignmentSourceFilter, CompetencyAssignmentStatusFilter, CompetencyLevelGap };
// Re-exported under the panel-local names every Competencies consumer already imports — the
// assignment tables' filter reads "Unassigned" where the admin tabs keep "Archived".
export const STATUS_LABELS = ASSIGNMENT_STATUS_LABELS;
export const STATUS_OPTIONS = ASSIGNMENT_STATUS_OPTIONS;
export { DEFAULT_STATUS };

export type SelectFilterValue = { id: string; name: string };

// Superset filter. My Competencies uses only source/levelGap; team adds assignee/competency/roles;
// company adds assignee/manager/competencies (multi)/roles.
export type CompetenciesFilter = {
	status?: CompetencyAssignmentStatusFilter;
	source?: CompetencyAssignmentSourceFilter;
	levelGap?: CompetencyLevelGap[];
	assignee?: SelectFilterValue;
	// Company page only — filters by the assignee's manager.
	manager?: SelectFilterValue;
	competency?: SelectFilterValue;
	// Company page only — multi-select Competency (the team page keeps singular `competency`).
	competencies?: SelectFilterValue[];
	// Multi-select: filters members by their OWN role (the "Role" custom field).
	roles?: SelectFilterValue[];
};

export const CompetenciesFilterFormSchema = z.object({
	status: z.enum(["any", "active", "archived"]),
	assignee: z.object({ id: z.string(), name: z.string() }).nullable(),
	manager: z.object({ id: z.string(), name: z.string() }).nullable(),
	competency: z.object({ id: z.string(), name: z.string() }).nullable(),
	competencies: z.array(z.object({ id: z.string(), name: z.string() })),
	roles: z.array(z.object({ id: z.string(), name: z.string() })),
	source: z.enum(["any", "role", "company_wide"]),
	exceeds: z.boolean(),
	meets: z.boolean(),
	below: z.boolean(),
	no_level: z.boolean(),
});
export type CompetenciesFilterForm = z.infer<typeof CompetenciesFilterFormSchema>;

export const SOURCE_OPTIONS: { value: CompetenciesFilterForm["source"]; label: string }[] = [
	{ value: "any", label: "Any" },
	{ value: "role", label: "Role Specific Competencies" },
	{ value: "company_wide", label: "Company Wide Competencies" },
];

export const SOURCE_LABELS: Record<"role" | "company_wide", string> = {
	role: "Role Specific",
	company_wide: "Company Wide",
};

export const LEVEL_GAP_KEYS = ["exceeds", "meets", "below", "no_level"] as const;

export const LEVEL_GAP_LABELS: Record<CompetencyLevelGap, string> = {
	exceeds: "Exceeds Expected Level",
	meets: "Meets Expected Level",
	below: "Below Expected Level",
	no_level: "Not assessed",
};
